import {
  Button,
  CardContent,
  Divider,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material"
import { useContext, useEffect, useState } from "react"
import { MainContext } from "../../controllers/main"
import EmbedSlice from "../../models/bodySlices/embedSlice"
import ImageSlice from "../../models/bodySlices/imageSlice"
import QuoteSlice from "../../models/bodySlices/quoteSlice"
import TextSlice from "../../models/bodySlices/textSlice"
import BlockEditor from "../blockEditor/blockEditor"
import { BodySliceType } from "../../services/config/enum"
import {
  deepCopy,
  isValidURL,
  stripHtml,
  testYtURL,
} from "../../services/utilities/utility"
import constraints from "../../services/config/constraints"
import { CheckInGroupsContext } from "../../controllers/checkInGroups"
import CheckInGroup from "../../models/checkInGroup"

const CheckInGroupTranslationsEdit = ({ itemIndex }: { itemIndex: number }) => {
  const { showPadding } = useContext(MainContext)
  const {
    currentCheckInGroup,
    setCurrentCheckInGroup,
    editMode,
    translationsErrors,
    setTranslationsErrors,
    copyDetailsFromDefault,
    copyBodyFromDefault,
  } = useContext(CheckInGroupsContext)

  // current item
  const currentItem = currentCheckInGroup.document.items[itemIndex]

  // errors managment
  const [titleError, setTitleError] = useState<boolean>(false)
  const [descriptionError, setDescriptionError] = useState<boolean>(false)
  const [bodyError, setBodyError] = useState<boolean>(false)

  // update translations errors on errors change
  useEffect(() => {
    if (titleError || descriptionError || bodyError) {
      translationsErrors.find(
        (item) => item.lang === currentItem.lang
      ).hasErrors = true
    } else {
      translationsErrors.find(
        (item) => item.lang === currentItem.lang
      ).hasErrors = false
    }

    setTranslationsErrors([...translationsErrors])
  }, [titleError, descriptionError, bodyError])

  // update errors on currentItem change
  useEffect(() => {
    if (currentItem.title.length < constraints.checkInGroup.title.min) {
      setTitleError(true)
    } else {
      setTitleError(false)
    }

    if (
      currentItem.description.length < constraints.checkInGroup.description.min
    ) {
      setDescriptionError(true)
    } else {
      setDescriptionError(false)
    }

    let errorInBody = false
    if (currentItem.body) {
      currentItem.body.forEach((bodyItem) => {
        if (
          (bodyItem.sliceType === BodySliceType.BodyTextSlice &&
            !stripHtml(bodyItem.text).length) ||
          (bodyItem.sliceType === BodySliceType.BodyQuoteSlice &&
            !bodyItem.quoteText.length) ||
          (bodyItem.sliceType === BodySliceType.BodyImageSlice &&
            (!bodyItem.imgUrl.length || !isValidURL(bodyItem.imgUrl))) ||
          (bodyItem.sliceType === BodySliceType.BodyEmbedSlice &&
            (!bodyItem.embedUrl.length || !testYtURL(bodyItem.embedUrl)))
        ) {
          errorInBody = true
        }
      })
    }
    if (errorInBody) {
      setBodyError(true)
    } else {
      setBodyError(false)
    }
  }, [currentItem])

  return (
    <CardContent
      style={{
        height:
          showPadding === "yes" ? "calc(100vh - 200px)" : "calc(100vh - 183px)",
        maxHeight:
          showPadding === "yes" ? "calc(100vh - 200px)" : "calc(100vh - 183px)",
        overflowY: "scroll",
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" style={{ height: 28 }}>
          <Typography variant="h6" className="card-title">
            Details
          </Typography>
          <Stack
            alignItems="center"
            justifyContent="flex-end"
            style={{ width: "100%" }}
            direction="row"
            spacing={2}
          >
            <FormGroup style={{ marginRight: -16 }}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!editMode}
                    checked={currentItem.isDefault}
                    onChange={(e) => {
                      const currentCheckInGroupCopy: CheckInGroup =
                        deepCopy(currentCheckInGroup)
                      currentCheckInGroupCopy.document.items[
                        itemIndex
                      ].isDefault = e.target.checked
                      if (e.target.checked) {
                        currentCheckInGroupCopy.document.items.forEach(
                          (item, index) => {
                            if (index !== itemIndex) {
                              item.isDefault = false
                            }
                          }
                        )
                      }
                      setCurrentCheckInGroup(currentCheckInGroupCopy)
                    }}
                  />
                }
                label="Default"
              />
            </FormGroup>
            <Divider orientation="vertical" flexItem />
            <Button
              size="small"
              variant="outlined"
              style={{ height: 26, fontSize: 12 }}
              onClick={() => {
                copyDetailsFromDefault(itemIndex)
              }}
              disabled={
                currentItem.isDefault ||
                !editMode ||
                !currentCheckInGroup.document.items.filter(
                  (item) => item.isDefault
                ).length
              }
            >
              Copy from default
            </Button>
          </Stack>
        </Stack>
        <Stack gap={2} direction="row">
          <TextField
            fullWidth
            size="small"
            label="Title"
            value={currentItem.title}
            onChange={(e) => {
              const currentCheckInGroupCopy: CheckInGroup =
                deepCopy(currentCheckInGroup)
              currentCheckInGroupCopy.document.items[itemIndex].title =
                e.target.value
              setCurrentCheckInGroup(currentCheckInGroupCopy)

              // errors check
              if (e.target.value.length < constraints.checkInGroup.title.min) {
                setTitleError(true)
              } else {
                setTitleError(false)
              }
            }}
            disabled={!editMode}
            error={titleError}
            helperText={
              currentItem.title.length < constraints.checkInGroup.title.min
                ? "This field is required"
                : ""
            }
            onBlur={() => {
              if (currentCheckInGroup.document.items[itemIndex].title) {
                const currentCheckInGroupCopy: CheckInGroup =
                  deepCopy(currentCheckInGroup)
                currentCheckInGroupCopy.document.items[itemIndex].title =
                  currentCheckInGroupCopy.document.items[itemIndex].title.trim()
                setCurrentCheckInGroup(currentCheckInGroupCopy)
              }
            }}
          />
        </Stack>
        <TextField
          fullWidth
          size="small"
          label="Description"
          value={currentItem.description}
          onChange={(e) => {
            const currentCheckInGroupCopy: CheckInGroup =
              deepCopy(currentCheckInGroup)
            currentCheckInGroupCopy.document.items[itemIndex].description =
              e.target.value
            setCurrentCheckInGroup(currentCheckInGroupCopy)

            // errors check
            if (
              e.target.value.length < constraints.checkInGroup.description.min
            ) {
              setDescriptionError(true)
            } else {
              setDescriptionError(false)
            }
          }}
          disabled={!editMode}
          error={descriptionError}
          helperText={
            currentItem.description.length <
            constraints.checkInGroup.description.min
              ? "This field is required"
              : ""
          }
          onBlur={() => {
            if (currentCheckInGroup.document.items[itemIndex].description) {
              const currentCheckInGroupCopy: CheckInGroup =
                deepCopy(currentCheckInGroup)
              currentCheckInGroupCopy.document.items[itemIndex].description =
                currentCheckInGroupCopy.document.items[
                  itemIndex
                ].description.trim()
              setCurrentCheckInGroup(currentCheckInGroupCopy)
            }
          }}
        />
        <Stack spacing={2}>
          <Stack direction="row" style={{ height: 28 }}>
            <Typography variant="h6" className="card-title">
              Body
            </Typography>
            <Stack
              alignItems="center"
              justifyContent="flex-end"
              style={{ width: "100%" }}
              direction="row"
              spacing={2}
            >
              <Button
                size="small"
                variant="outlined"
                style={{ height: 26, fontSize: 12 }}
                onClick={() => {
                  copyBodyFromDefault(itemIndex)
                }}
                disabled={
                  currentItem.isDefault ||
                  !editMode ||
                  !currentCheckInGroup.document.items.filter(
                    (item) => item.isDefault
                  ).length
                }
              >
                Copy from default
              </Button>
            </Stack>
          </Stack>
          <BlockEditor
            body={currentItem.body ?? []}
            setBody={(
              body: (EmbedSlice | ImageSlice | QuoteSlice | TextSlice)[]
            ) => {
              const currentCheckInGroupCopy: CheckInGroup =
                deepCopy(currentCheckInGroup)
              currentCheckInGroupCopy.document.items[itemIndex].body = body
              setCurrentCheckInGroup(currentCheckInGroupCopy)
            }}
            editMode={editMode}
          />
        </Stack>
      </Stack>
    </CardContent>
  )
}

export default CheckInGroupTranslationsEdit
